
import { CheckCircle, AlertCircle, Info, Clock, AlertTriangle } from 'lucide-react';
import { ImportResult as ImportResultType } from './types';
import { ValidationDetails } from './ValidationDetails';

interface ImportResultProps {
  result: ImportResultType;
}

export const ImportResult = ({ result }: ImportResultProps) => {
  return (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg flex items-start space-x-3 ${
        result.success 
          ? 'bg-green-50 text-green-800 border border-green-200' 
          : 'bg-red-50 text-red-800 border border-red-200'
      }`}>
        {result.success ? (
          <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1">
          <p className="font-medium">{result.message}</p>
          
          {result.success && (
            <div className="mt-3 space-y-2">
              {/* Main stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white bg-opacity-50 rounded p-2">
                  <div className="font-semibold text-green-900">
                    {result.imported || 0} importées
                  </div>
                  <div className="text-xs text-green-700">Nouvelles annonces</div>
                </div>
                <div className="bg-white bg-opacity-50 rounded p-2">
                  <div className="font-semibold text-green-900">
                    {result.total_scraped || 0} trouvées
                  </div>
                  <div className="text-xs text-green-700">Total sur les sites</div>
                </div>
              </div>
              
              {/* Validation stats */}
              {result.validation_stats && (
                <div className="bg-white bg-opacity-50 rounded p-3 mt-3">
                  <h4 className="font-medium text-green-900 mb-2 flex items-center">
                    <Info className="h-4 w-4 mr-1" />
                    Validation des données
                  </h4>
                  <div className="flex space-x-4 text-xs">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                      <span>{result.validation_stats.valid} valides</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                      <span>{result.validation_stats.incomplete} incomplètes</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                      <span>{result.validation_stats.invalid} invalides</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Duplicates info */}
              {result.skipped_duplicates !== undefined && result.skipped_duplicates > 0 && (
                <div className="flex items-center space-x-2 text-blue-700 bg-blue-50 px-3 py-2 rounded border border-blue-200">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">
                    {result.skipped_duplicates} doublon(s) ignoré(s) - déjà présent(s) en base
                  </span>
                </div>
              )}
              
              {/* Processing time */}
              {result.processing_time && (
                <div className="flex items-center space-x-2 text-gray-600 text-xs">
                  <Clock className="h-3 w-3" />
                  <span>Traitement effectué en {Math.round(result.processing_time / 1000)}s</span>
                </div>
              )}
              
              {/* Scraping errors */}
              {result.scraping_errors && result.scraping_errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                  <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Avertissements de scraping
                  </h4>
                  <ul className="text-xs text-yellow-800 space-y-1">
                    {result.scraping_errors.map((error, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-1">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {!result.success && result.error && (
            <div className="mt-2">
              <p className="text-sm font-medium">Détail de l'erreur :</p>
              <p className="text-xs mt-1 bg-red-100 p-2 rounded">{result.error}</p>
              {result.scraping_errors && result.scraping_errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Erreurs de scraping :</p>
                  <ul className="text-xs mt-1 space-y-1">
                    {result.scraping_errors.map((error, index) => (
                      <li key={index} className="bg-red-100 p-1 rounded">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ValidationDetails details={result.details} />
    </div>
  );
};
